import { AlertCircle, FileVideo, Filter, MessageSquare } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Id } from "@cvx/_generated/dataModel";
import StatusBadge from "@/components/Generic/StatusBadge";
import { api } from "@cvx/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { useAccount } from "@/state/useAccount";
import { useQuery } from "@tanstack/react-query";

// Custom stat card component for video status counts
const VideoStatusCard = ({
  title,
  count,
  subtitle,
  icon,
}: {
  title: string;
  count: number;
  subtitle: string;
  icon: React.ReactNode;
}) => {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-primary/60">{title}</h3>
        {icon}
      </div>
      <div className="mt-2">
        <p className="text-3xl font-semibold text-primary">{count}</p>
      </div>
      <div className="mt-1">
        <p className="text-sm text-primary/60">{subtitle}</p>
      </div>
    </Card>
  );
};

// Define video status type
type VideoStatus =
  | "draft"
  | "ai_reviewing"
  | "ai_approved"
  | "ai_rejected"
  | "team_reviewing"
  | "team_rejected"
  | "needs_review"
  | "brand_reviewing"
  | "approved"
  | "rejected";
type ContractStatus = "pendingApproval" | "active" | "completed" | "terminated";

function VideosOverview() {
  const { appId } = useAccount();
  const navigate = useNavigate();
  const search = useSearch({
    from: "/_app/_auth/dashboard/_layout/videosoverview",
  });

  // State for video status filter
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  // State for contract status filter
  const [selectedContractStatuses, setSelectedContractStatuses] = useState<
    string[]
  >(["active"]);
  const [hasInitialized, setHasInitialized] = useState(false);

  const { data: videoDashboardData } = useQuery(
    convexQuery(api.dashboard.getAppFoundersVideoDashboard, {
      appId: appId as Id<"apps">,
    }),
  );

  // Get all unique video statuses
  const allStatuses: VideoStatus[] = Array.from(
    new Set(
      videoDashboardData?.videoProjectsDetails
        .map((video) => video.status)
        .filter((status): status is VideoStatus =>
          [
            "draft",
            "ai_reviewing",
            "ai_approved",
            "ai_rejected",
            "team_reviewing",
            "team_rejected",
            "brand_reviewing",
            "approved",
            "rejected",
          ].includes(status),
        ) || [],
    ),
  );

  // Get all unique contract statuses
  const allContractStatuses: ContractStatus[] = Array.from(
    new Set(
      videoDashboardData?.videoProjectsDetails
        .map((video) => video.contractStatus)
        .filter((status): status is ContractStatus =>
          ["pendingApproval", "active", "completed", "terminated"].includes(
            status,
          ),
        ) || [],
    ),
  );

  // Initialize selectedStatuses when data loads (only once)
  useEffect(() => {
    if (
      allStatuses.length > 0 &&
      allContractStatuses.length > 0 &&
      !hasInitialized
    ) {
      // If we have a status in the URL, use that
      if (search.status && allStatuses.includes(search.status as VideoStatus)) {
        setSelectedStatuses([search.status]);
      } else {
        // Otherwise use default unpublished statuses
        const defaultUnpublishedStatuses = [
          "draft",
          "ai_reviewing",
          "ai_approved",
          "team_reviewing",
          "brand_reviewing",
        ];
        const availableDefaultStatuses = defaultUnpublishedStatuses.filter(
          (status) => allStatuses.includes(status as VideoStatus),
        );
        setSelectedStatuses(availableDefaultStatuses);
      }

      // Filter selectedContractStatuses to only include statuses that actually exist in the data
      if (allContractStatuses.includes("active")) {
        setSelectedContractStatuses(["active"]);
      } else {
        setSelectedContractStatuses([...allContractStatuses]);
      }

      setHasInitialized(true);
    }
  }, [
    allStatuses.length,
    allContractStatuses.length,
    hasInitialized,
    search.status,
  ]);

  // Calculate total counts for videos from selected contract statuses (for stat cards)
  const allVideos = videoDashboardData?.videoProjectsDetails || [];
  const videosFromSelectedContracts = allVideos.filter((video) =>
    selectedContractStatuses.includes(video.contractStatus),
  );
  const totalDraftCount = videosFromSelectedContracts.filter(
    (video) => video.status === "draft",
  ).length;
  const totalReviewCount = videosFromSelectedContracts.filter(
    (video) =>
      video.status === "ai_reviewing" ||
      video.status === "team_reviewing" ||
      video.status === "brand_reviewing",
  ).length;
  const totalRejectedCount = videosFromSelectedContracts.filter(
    (video) =>
      video.status === "rejected" ||
      video.status === "ai_rejected" ||
      video.status === "team_rejected",
  ).length;
  const totalApprovedCount = videosFromSelectedContracts.filter(
    (video) => video.status === "approved",
  ).length;

  // Filter video projects based on selected statuses (for table only)
  const filteredVideos = allVideos.filter(
    (video) =>
      selectedStatuses.includes(video.status) &&
      selectedContractStatuses.includes(video.contractStatus),
  );

  const handleVideoClick = (video: (typeof filteredVideos)[0]) => {
    // Navigate to the video feedback page using the contract ID
    navigate({
      to: "/partnerships/$partnershipId",
      params: { partnershipId: video.contractId },
      search: {
        tab: "videos",
        projectId: video.projectId,
      },
    });
  };

  const handleStatusToggle = (status: string, checked: boolean) => {
    if (checked) {
      setSelectedStatuses((prev) => [...prev, status]);
    } else {
      setSelectedStatuses((prev) => prev.filter((s) => s !== status));
    }
  };

  const handleSelectAll = () => {
    setSelectedStatuses([...allStatuses]);
  };

  const handleSelectNone = () => {
    setSelectedStatuses([]);
  };

  const handleContractStatusToggle = (status: string, checked: boolean) => {
    if (checked) {
      setSelectedContractStatuses((prev) => [...prev, status]);
    } else {
      setSelectedContractStatuses((prev) => prev.filter((s) => s !== status));
    }
  };

  const handleSelectAllContracts = () => {
    setSelectedContractStatuses([...allContractStatuses]);
  };

  const handleSelectNoContracts = () => {
    setSelectedContractStatuses([]);
  };

  const formatStatusLabel = (status: string) => {
    switch (status) {
      case "draft":
        return "Draft";
      case "ai_reviewing":
      case "team_reviewing":
      case "brand_reviewing":
        return "In Review";
      case "approved":
      case "ai_approved":
        return "Approved";
      case "rejected":
      case "ai_rejected":
      case "team_rejected":
        return "Rejected";
      default:
        return (
          status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ")
        );
    }
  };

  return (
    <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
      <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col gap-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <VideoStatusCard
            title="Total Drafts"
            count={totalDraftCount}
            subtitle="Videos in draft status"
            icon={<FileVideo className="h-5 w-5 text-primary/60" />}
          />
          <VideoStatusCard
            title="Rejected"
            count={totalRejectedCount}
            subtitle="Videos that were rejected"
            icon={<AlertCircle className="h-5 w-5 text-primary/60" />}
          />
          <VideoStatusCard
            title="In Review"
            count={totalReviewCount}
            subtitle="Pending company feedback"
            icon={<MessageSquare className="h-5 w-5 text-primary/60" />}
          />
          <VideoStatusCard
            title="Approved"
            count={totalApprovedCount}
            subtitle="Videos ready for publishing"
            icon={<FileVideo className="h-5 w-5 text-primary/60" />}
          />
        </div>

        {/* Videos Table with Filter */}
        <div className="rounded-lg border border-border bg-card">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-primary">
                  Video Projects ({filteredVideos.length})
                </h2>
                <p className="text-sm text-primary/60 mt-1">
                  All video projects matching selected statuses
                </p>
              </div>

              {/* Combined Filter */}
              {(allStatuses.length > 0 || allContractStatuses.length > 0) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Filter className="h-4 w-4 mr-2" />
                      Filters (
                      {selectedStatuses.length +
                        selectedContractStatuses.length}
                      /{allStatuses.length + allContractStatuses.length})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    {/* Video Status Section */}
                    {allStatuses.length > 0 && (
                      <>
                        <DropdownMenuLabel>Video Status</DropdownMenuLabel>

                        <div className="flex items-center justify-between px-2 py-1">
                          <Button
                            onClick={handleSelectAll}
                            variant="ghost"
                            size="sm"
                            className="text-xs h-6 px-2"
                          >
                            All Videos
                          </Button>
                          <Button
                            onClick={handleSelectNone}
                            variant="ghost"
                            size="sm"
                            className="text-xs h-6 px-2"
                          >
                            None
                          </Button>
                        </div>

                        {allStatuses.map((status) => (
                          <DropdownMenuCheckboxItem
                            key={`video-${status}`}
                            checked={selectedStatuses.includes(status)}
                            onCheckedChange={(checked) =>
                              handleStatusToggle(status, checked)
                            }
                          >
                            <div className="flex items-center space-x-2">
                              <StatusBadge status={status} />
                              <span className="sr-only">
                                {formatStatusLabel(status)}
                              </span>
                            </div>
                          </DropdownMenuCheckboxItem>
                        ))}
                      </>
                    )}

                    {/* Separator between sections */}
                    {allStatuses.length > 0 &&
                      allContractStatuses.length > 0 && (
                        <DropdownMenuSeparator />
                      )}

                    {/* Contract Status Section */}
                    {allContractStatuses.length > 0 && (
                      <>
                        <DropdownMenuLabel>Contract Status</DropdownMenuLabel>

                        <div className="flex items-center justify-between px-2 py-1">
                          <Button
                            onClick={handleSelectAllContracts}
                            variant="ghost"
                            size="sm"
                            className="text-xs h-6 px-2"
                          >
                            All Contracts
                          </Button>
                          <Button
                            onClick={handleSelectNoContracts}
                            variant="ghost"
                            size="sm"
                            className="text-xs h-6 px-2"
                          >
                            None
                          </Button>
                        </div>

                        {allContractStatuses.map((status) => (
                          <DropdownMenuCheckboxItem
                            key={`contract-${status}`}
                            checked={selectedContractStatuses.includes(status)}
                            onCheckedChange={(checked) =>
                              handleContractStatusToggle(status, checked)
                            }
                          >
                            <div className="flex items-center space-x-2">
                              <StatusBadge status={status} />
                              <span className="sr-only">
                                {status.replace(/([A-Z])/g, " $1").trim()}
                              </span>
                            </div>
                          </DropdownMenuCheckboxItem>
                        ))}
                      </>
                    )}

                    <DropdownMenuSeparator />

                    <div className="px-2 py-1 text-xs text-muted-foreground">
                      Showing {filteredVideos.length} of{" "}
                      {videoDashboardData?.videoProjectsDetails.length || 0}{" "}
                      videos
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            {filteredVideos.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <FileVideo className="h-12 w-12 text-primary/20 mb-4" />
                <p className="text-lg text-primary/60 mb-2">
                  {selectedStatuses.length === 0
                    ? "No video status selected"
                    : "No videos found"}
                </p>
                <p className="text-sm text-primary/40">
                  {selectedStatuses.length === 0
                    ? "Please select at least one video status to view projects"
                    : "No video projects match the selected status filters"}
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="border-b border-border bg-secondary">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                      Video Title
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                      Creator
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                      Contract Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredVideos.map((video) => (
                    <tr
                      key={video.projectId}
                      className="hover:bg-secondary/50 cursor-pointer"
                      onClick={() => handleVideoClick(video)}
                    >
                      <td className="px-6 py-4">
                        <span className="text-sm font-medium text-primary">
                          {video.projectTitle}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-primary">
                          {video.creatorName}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={video.status as VideoStatus} />
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge
                          status={
                            video.contractStatus as
                            | "pendingApproval"
                            | "active"
                            | "completed"
                            | "terminated"
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/videosoverview",
)({
  component: VideosOverview,
  beforeLoad: () => ({
    title: "Videos Overview",
    headerTitle: "Videos Overview",
    headerDescription: "Track the status of all video content projects",
  }),
  validateSearch: (search: Record<string, unknown>) => ({
    status: search.status as string | undefined,
  }),
});
